import { Loader, type LoaderShape } from '../Loader'

import { type JSX, Show, splitProps } from 'solid-js'

export type ButtonVariant = 'super-primary' | 'primary' | 'ai' | 'ghost' | 'text' | 'quiet' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'toolbar'
export type ButtonTone = 'danger' | 'success'

export interface ButtonProps extends JSX.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  fullWidth?: boolean
  danger?: boolean
  tone?: ButtonTone
  onTint?: boolean
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-1 text-[length:var(--text-sm)] min-h-9 rounded-[var(--radius-12)]',
  md: 'px-5 py-1 text-[length:var(--text-sm)] min-h-10 rounded-[var(--radius-12)]',
  lg: 'px-6 py-2 text-[length:var(--text-base)] min-h-12 rounded-[var(--radius-16)]',
  toolbar: 'px-5 py-1 text-[length:var(--text-sm)] min-h-12 rounded-[var(--radius-16)]',
}

const loaderSizes: Record<ButtonSize, number> = { sm: 32, md: 36, lg: 40, toolbar: 36 }

const LINK_LOADER_SIZE = 16

const LOADING_LINK_CLASSES =
  'bg-transparent text-[color:var(--color-text-muted)] focus-visible:ring-[var(--color-primary-500)]'

const linkSizeClasses: Record<ButtonSize, string> = {
  sm: 'py-0.5 text-[length:var(--text-sm)] rounded-[var(--radius-sm)]',
  md: 'py-0.5 text-[length:var(--text-base)] rounded-[var(--radius-sm)]',
  lg: 'py-0.5 text-[length:var(--text-lg)] rounded-[var(--radius-sm)]',
  toolbar: 'py-0.5 text-[length:var(--text-base)] rounded-[var(--radius-sm)]',
}

const quietSizeClasses: Record<ButtonSize, string> = {
  sm: 'py-0.5 text-[length:var(--text-caption)] rounded-[var(--radius-sm)]',
  md: 'py-0.5 text-[length:var(--text-sm)] rounded-[var(--radius-sm)]',
  lg: 'py-0.5 text-[length:var(--text-base)] rounded-[var(--radius-sm)]',
  toolbar: 'py-0.5 text-[length:var(--text-sm)] rounded-[var(--radius-sm)]',
}

const isInline = (variant: ButtonVariant) => variant === 'link' || variant === 'quiet'

function getSizeClasses(variant: ButtonVariant, size: ButtonSize): string {
  if (variant === 'link') {
    return linkSizeClasses[size]
  }
  if (variant === 'quiet') {
    return quietSizeClasses[size]
  }
  return sizeClasses[size]
}

function getVariantClasses(
  variant: ButtonVariant,
  tone: ButtonTone | undefined,
  onTint = false
): string {
  if (tone === 'danger') {
    switch (variant) {
      case 'super-primary':
        return 'bg-[var(--color-error-600)] text-[color:var(--color-on-error)] hover:bg-[var(--color-error-700)] focus-visible:ring-[var(--color-error-600)] hover:shadow-[var(--shadow-hover-error)]'
      case 'primary':
        return 'bg-[var(--color-error-700)] text-[color:var(--color-on-error)] hover:bg-[var(--color-error-800)] dark:bg-[var(--color-error)] dark:hover:bg-[var(--color-error-600)] focus-visible:ring-[var(--color-error-700)] hover:shadow-[var(--shadow-hover)]'
      case 'ghost':
        return 'border border-[var(--color-error-200)] bg-transparent text-[color:var(--color-error-600)] hover:not-disabled:bg-[var(--color-error-50)] hover:not-disabled:border-[var(--color-error)] focus-visible:ring-[var(--color-error-600)]'
      case 'text':
      case 'quiet':
      case 'link':
        return 'bg-transparent text-[color:var(--color-error-600)] ds-link-shimmer focus-visible:ring-[var(--color-error-600)]'
    }
  }

  if (tone === 'success') {
    switch (variant) {
      case 'super-primary':
      case 'primary':
        return 'bg-[var(--color-success-700)] text-[color:var(--color-on-success)] hover:bg-[var(--color-success-800)] focus-visible:ring-[var(--color-success-700)] hover:shadow-[var(--shadow-hover)]'
      case 'ghost':
        return 'border border-[var(--color-success-200)] bg-transparent text-[color:var(--color-success-700)] hover:not-disabled:bg-[var(--color-success-50)] hover:not-disabled:border-[var(--color-success-600)] focus-visible:ring-[var(--color-success-700)]'
      case 'ai':
      case 'text':
      case 'quiet':
      case 'link':
        return 'bg-transparent text-[color:var(--color-success-700)] ds-link-shimmer focus-visible:ring-[var(--color-success-700)]'
    }
  }

  switch (variant) {
    case 'super-primary':
      return [
        'bg-[var(--color-primary-500)] text-[var(--color-on-primary)]',
        'hover:bg-[var(--color-primary-600)]',
        'hover:shadow-[var(--shadow-hover-primary)]',
        'focus-visible:ring-[var(--color-primary-500)]',
        'active:bg-[var(--color-primary-700)]',
        'relative overflow-hidden',
      ].join(' ')
    case 'primary':
      return [
        'bg-[var(--color-primary-dark)] text-[color:var(--color-primary-dark-text)]',
        'hover:bg-[var(--color-primary-dark-hover)]',
        'hover:shadow-[var(--shadow-hover)]',
        'focus-visible:ring-[var(--color-primary-dark)]',
      ].join(' ')
    case 'ai':
      return [
        'bg-[linear-gradient(110deg,var(--ai-button-from),var(--ai-button-mid)_55%,var(--ai-button-to))]',
        'text-[var(--ai-button-ink)]',
        'shadow-[0_1px_2px_rgba(16,20,32,.16),0_6px_18px_-6px_var(--ai-button-glow)]',
        'hover:not-disabled:shadow-[0_2px_4px_rgba(16,20,32,.18),0_10px_26px_-8px_var(--ai-button-glow)]',
        'focus-visible:ring-[var(--ai-button-mid)]',
        'relative overflow-hidden isolate',
      ].join(' ')
    case 'ghost':
      if (onTint) {
        return [
          'border border-[var(--color-neutral-300)] bg-transparent text-[color:var(--color-text)]',
          'hover:not-disabled:bg-[color-mix(in_srgb,var(--color-text)_6%,transparent)] hover:not-disabled:border-[var(--color-neutral-400)]',
          'focus-visible:ring-[var(--color-primary-500)]',
        ].join(' ')
      }
      return [
        'border border-[var(--color-field-border)] bg-[var(--color-surface)] text-[color:var(--color-text)]',
        'hover:not-disabled:bg-[var(--color-ghost-hover-fill)] hover:not-disabled:border-[var(--color-ghost-hover-border)]',
        'focus-visible:ring-[var(--color-primary-500)]',
      ].join(' ')
    case 'text':
      return [
        'bg-transparent text-[color:var(--color-text)]',
        'ds-link-shimmer',
        'focus-visible:ring-[var(--color-primary-500)]',
      ].join(' ')
    case 'quiet':
      return [
        'bg-transparent text-[color:var(--color-text-muted)]',
        'hover:not-disabled:text-[color:var(--color-text)] ds-link-shimmer',
        'focus-visible:ring-[var(--color-primary-500)]',
      ].join(' ')
    case 'link':
      return [
        'bg-transparent text-[color:var(--color-primary-600)]',
        'hover:text-[color:var(--color-primary-700)] ds-link-shimmer',
        'focus-visible:ring-[var(--color-primary-500)]',
      ].join(' ')
  }
}

function getHoverTransform(variant: ButtonVariant): string {
  if (variant === 'text' || isInline(variant)) {
    return ''
  }
  if (variant === 'super-primary') {
    return 'motion-safe:hover:not-disabled:translate-y-[-1px]'
  }
  return 'motion-safe:hover:not-disabled:translate-y-[-1px]'
}

function getPressTransform(variant: ButtonVariant): string {
  if (isInline(variant)) {
    return ''
  }
  return 'motion-safe:active:not-disabled:translate-y-0 motion-safe:active:not-disabled:scale-[0.98]'
}

function disabledLook(variant: ButtonVariant, tone: ButtonTone | undefined): string {
  if (variant === 'primary' || variant === 'super-primary') {
    return 'disabled:opacity-(--opacity-fill-disabled)'
  }
  if (tone) {
    return 'disabled:opacity-50'
  }
  if (variant === 'ai') {
    return 'disabled:opacity-(--opacity-fill-disabled) disabled:shadow-none disabled:saturate-[.3]'
  }
  return 'disabled:opacity-50'
}

export interface ButtonClassOptions {
  variant: ButtonVariant
  size: ButtonSize
  tone?: ButtonTone | undefined
  onTint?: boolean | undefined
  fullWidth?: boolean | undefined
  loading?: boolean | undefined
  class?: string | undefined
}

export function buttonClass(options: ButtonClassOptions): string {
  return `
        ds-button inline-flex items-center ${isInline(options.variant) ? 'justify-start text-start' : 'justify-center text-center'} normal-case gap-2 cursor-pointer [&>svg]:shrink-0
        ${options.variant === 'quiet' ? 'font-[var(--font-normal)]' : 'font-[var(--font-semibold)]'} leading-normal
        transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-surface)]
        ${options.loading ? 'disabled:cursor-wait' : `${disabledLook(options.variant, options.tone)} disabled:cursor-not-allowed`}
        ${getHoverTransform(options.variant)}
        ${getPressTransform(options.variant)}
        ${
          options.variant === 'link' && options.loading
            ? LOADING_LINK_CLASSES
            : getVariantClasses(options.variant, options.tone, options.onTint === true)
        }
        ${
          options.variant === 'ai' && !options.tone
            ? `after:pointer-events-none after:absolute after:inset-0 after:-translate-x-full
               after:bg-[linear-gradient(100deg,transparent,var(--ai-button-sheen),transparent)]
               motion-safe:hover:not-disabled:after:translate-x-full
               after:transition-transform after:duration-[850ms] after:ease-[var(--ease-out)]`
            : ''
        }
        ${getSizeClasses(options.variant, options.size)}
        ${options.fullWidth ? 'w-full' : ''}
        ${options.class ?? ''}
      `.trim()
}

export function Button(props: ButtonProps) {
  const [local, rest] = splitProps(props, [
    'variant',
    'size',
    'loading',
    'fullWidth',
    'danger',
    'tone',
    'onTint',
    'disabled',
    'class',
    'children',
  ])

  const variant = () => local.variant ?? 'primary'
  const size = () => local.size ?? 'md'
  const tone = () => local.tone ?? (local.danger ? 'danger' : undefined)
  const isDisabled = () => local.disabled || local.loading

  return (
    <button
      {...rest}
      disabled={isDisabled()}
      aria-busy={local.loading || undefined}
      class={buttonClass({
        variant: variant(),
        size: size(),
        tone: tone(),
        onTint: local.onTint,
        fullWidth: local.fullWidth,
        loading: local.loading,
        class: local.class,
      })}
    >
      <Show when={local.loading && !isInline(variant())}>
        <ButtonLoader width={loaderSizes[size()]} shape="cascade" />
      </Show>
      {local.children}
      <Show when={local.loading && isInline(variant())}>
        <ButtonLoader width={LINK_LOADER_SIZE} shape="radial" />
      </Show>
    </button>
  )
}

function ButtonLoader(props: { width: number; shape: LoaderShape }) {
  return (
    <span
      class="relative inline-flex h-[1em] shrink-0 items-center justify-center"
      style={{ width: `${props.width}px` }}
      aria-hidden="true"
    >
      <Loader
        size={props.width}
        tone="current"
        shape={props.shape}
        class="absolute top-1/2 -translate-y-1/2"
      />
    </span>
  )
}
